// apps/client/src/pages/OfficeComparisonPage.tsx
// Side-by-side office performance comparison with real data

import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
// Note: collection and getDocs are used by OfficeDataFetcher for broker count queries
import { useMonthlyAggregates } from '../hooks/useMonthlyAggregates';
import { useRolling3RunWindow } from '../hooks/useRolling3RunWindow';
import { useCompanyDataYearRange } from '../hooks/useCompanyDataYearRange';
import { useHideCommissions, useHideLoanVolume } from '../hooks/useHideCommissions';
import { adminPageDenied } from '../config/adminAccess';
import { formatCurrencyShorthand } from '../utils/formatters';
import { GitCompare } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { resolveLoanBookSelection } from '../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import type { Office, UserData, CompanyData } from '../types';

interface DashboardContext {
  userData: UserData;
  companyData: CompanyData;
  userVisibleOffices: Office[];
}

interface OfficeData {
  office: Office | undefined;
  officeId: string;
  upfrontTotal: number;
  trailTotal: number;
  totalCommission: number;
  totalLoanBook: number;
  lineOfTrail: number;
  brokerCount: number;
  loading: boolean;
}

// Child component that fetches data for a single office
// Uses EXACT SAME approach as OfficeDashboardPage (lines 84-145)
const OfficeDataFetcher: React.FC<{
  officeId: string;
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  entityCode?: string;
  onDataLoaded: (_officeId: string, _data: Omit<OfficeData, 'office' | 'officeId'>) => void;
}> = ({ officeId, companyId, year, yearType, entityCode, onDataLoaded }) => {
  // ✅ EXACT SAME as OfficeDashboardPage line 84-92
  // Use monthly aggregates for commission data (supports year filtering)
  const { aggregates, loading: aggLoading } = useMonthlyAggregates({
    companyId,
    year,
    yearType,
    officeId,  // Pass office filter to hook
    entityCode,
  });

  // ✅ Use year-based loan book/line of trail with office filtering
  // Respects calendar/financial year filter for consistent comparison
  const {
    totalLoanBook,
    totalLineOfTrail: lineOfTrail,
    loading: loadingLoanBook
  } = useRolling3RunWindow({
    companyId,
    officeId,
    entityCode,
    yearMode: {
      year,
      yearType,
    },
  });

  const [brokerCount, setBrokerCount] = useState(0);
  const [brokersLoading, setBrokersLoading] = useState(true);

  // ✅ EXACT SAME as OfficeDashboardPage line 183-189
  const upfrontCommission = React.useMemo(() => {
    return aggregates.reduce((sum, agg) => sum + agg.upfrontCommission, 0);
  }, [aggregates]);

  const trailCommission = React.useMemo(() => {
    return aggregates.reduce((sum, agg) => sum + agg.trailCommission, 0);
  }, [aggregates]);

  // Fetch broker count for this office
  useEffect(() => {
    const fetchBrokerCount = async () => {
      try {
        const brokersQuery = query(
          collection(db, `companies/${companyId}/brokers`),
          where('currentOfficeId', '==', officeId),
          where('status', '==', 'active')
        );
        const snapshot = await getDocs(brokersQuery);
        // Filter out merged brokers client-side (mergedInto field may not exist on all docs)
        const activeBrokerCount = snapshot.docs.filter(doc => !doc.data().mergedInto).length;
        setBrokerCount(activeBrokerCount);
      } catch (err) {
        console.error(`Error fetching broker count for office ${officeId}:`, err);
        setBrokerCount(0);
      } finally {
        setBrokersLoading(false);
      }
    };

    fetchBrokerCount();
  }, [companyId, officeId]);

  // Report data back to parent when all loading is complete
  useEffect(() => {
    const loading = aggLoading || loadingLoanBook || brokersLoading;
    onDataLoaded(officeId, {
      upfrontTotal: upfrontCommission,
      trailTotal: trailCommission,
      totalCommission: upfrontCommission + trailCommission,
      totalLoanBook: totalLoanBook, // Already in dollars from upload_aggregates
      lineOfTrail,
      brokerCount,
      loading,
    });
  }, [
    officeId,
    upfrontCommission,
    trailCommission,
    totalLoanBook,
    lineOfTrail,
    brokerCount,
    aggLoading,
    loadingLoanBook,
    brokersLoading,
    onDataLoaded,
  ]);

  return null; // This component doesn't render anything
};

const OfficeComparisonPage: React.FC = () => {
  const context = useOutletContext<DashboardContext & { loanBooks?: ResolvedLoanBook[] }>();
  const { userData, companyData, userVisibleOffices } = (context || {}) as DashboardContext;
  const loanBooks = ((context as any)?.loanBooks || []) as ResolvedLoanBook[];
  // An administrator denied the dashboard commission figures sees this page's
  // commission columns hidden exactly as a hidden-commission user does.
  const hideCommissions = useHideCommissions() || adminPageDenied(userData, 'dashboard-commissions');
  const hideLoanVolume = useHideLoanVolume();

  const hasEntityLoanBooks = React.useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = React.useState('all');
  const resolvedParams = React.useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  const [selectedOffices, setSelectedOffices] = useState<string[]>([]);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Initialize year based on yearType - for financial year, use current FY
  const [yearType, setYearType] = useState<'calendar' | 'financial'>('calendar');
  const [year, setYear] = useState(currentYear);

  // Store fetched data for each office
  const [officeDataMap, setOfficeDataMap] = useState<Record<string, Omit<OfficeData, 'office' | 'officeId'>>>({});

  // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  // Callback for child components to report their data
  const handleDataLoaded = useCallback((officeId: string, data: Omit<OfficeData, 'office' | 'officeId'>) => {
    setOfficeDataMap(prev => ({
      ...prev,
      [officeId]: data,
    }));
  }, []);

  // Generate year options dynamically using BigQuery data range
  const yearOptions = React.useMemo(() => {
    const years: { value: number; label: string }[] = [];
    // Use dynamic range from BigQuery, always include current year
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);

    if (yearType === 'financial') {
      // For financial years, start from current FY and go back
      const startYear = currentMonth >= 6 ? dynamicLatestYear : dynamicLatestYear - 1;
      for (let y = startYear; y >= dynamicEarliestYear; y--) {
        years.push({ value: y, label: `${y}/${y + 1}` });
      }
    } else {
      // Calendar years: from latest to earliest
      for (let y = dynamicLatestYear; y >= dynamicEarliestYear; y--) {
        years.push({ value: y, label: String(y) });
      }
    }
    return years;
  }, [yearType, currentYear, currentMonth, earliestYear, latestYear]);

  // Handle year type change - adjust selected year appropriately
  const handleYearTypeChange = (newYearType: 'calendar' | 'financial') => {
    setYearType(newYearType);
    if (newYearType === 'financial') {
      // Switch to current financial year
      setYear(currentMonth >= 6 ? currentYear : currentYear - 1);
    } else {
      // Switch to current calendar year
      setYear(currentYear);
    }
  };

  // Combine office metadata with fetched data
  const officeData: OfficeData[] = selectedOffices.map(officeId => {
    const office = userVisibleOffices?.find(o => o.id === officeId);
    const data = officeDataMap[officeId] || {
      upfrontTotal: 0,
      trailTotal: 0,
      totalCommission: 0,
      totalLoanBook: 0,
      lineOfTrail: 0,
      brokerCount: 0,
      loading: true,
    };
    return {
      office,
      officeId,
      ...data,
    };
  });

  const handleOfficeToggle = (officeId: string) => {
    setSelectedOffices(prev =>
      prev.includes(officeId)
        ? prev.filter(id => id !== officeId)
        : [...prev, officeId].slice(0, 4) // Max 4 offices
    );
  };

  if (!context || !userData || !companyData) {
    return (
      <div className="p-8 text-center text-white">
        <p className="text-red-400">Failed to load user context</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={GitCompare}
        title="Office Comparison"
        subtitle="Compare performance metrics across offices"
      />

      <div>
      {/* Office selection */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Select Offices to Compare (max 4)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {userVisibleOffices?.map(office => (
            <button
              key={office.id}
              onClick={() => handleOfficeToggle(office.id)}
              disabled={!selectedOffices.includes(office.id) && selectedOffices.length >= 4}
              className={`p-3 rounded-lg text-left transition-colors ${
                selectedOffices.includes(office.id)
                  ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="font-medium">{office.name}</div>
              <div className="text-sm opacity-75">Office</div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">Year Type</label>
          <div className="flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
            <button
              onClick={() => handleYearTypeChange('calendar')}
              className={`px-3 py-1 text-sm rounded-md ${yearType === 'calendar' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
            >
              Calendar
            </button>
            <button
              onClick={() => handleYearTypeChange('financial')}
              className={`px-3 py-1 text-sm rounded-md ${yearType === 'financial' ? 'bg-blue-600 text-white' : 'text-gray-300'}`}
            >
              Financial
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2">Year</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2"
          >
            {yearOptions.map(y => (
              <option key={y.value} value={y.value}>{y.label}</option>
            ))}
          </select>
        </div>

        {/* Loan Book Selector — unified-mode companies only */}
        {hasEntityLoanBooks && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Loan Book</label>
            <select
              value={selectedLoanBook}
              onChange={(e) => setSelectedLoanBook(e.target.value)}
              className="block bg-gray-700 border-gray-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Loan Books</option>
              {(loanBooks as ResolvedLoanBook[]).map((lb: ResolvedLoanBook) => (
                <option key={lb.id} value={lb.id}>{lb.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Render data fetchers for each selected office */}
      {selectedOffices.map(officeId => (
        <OfficeDataFetcher
          key={officeId}
          officeId={officeId}
          companyId={userData.companyId}
          year={year}
          yearType={yearType}
          entityCode={resolvedParams.entityCode}
          onDataLoaded={handleDataLoaded}
        />
      ))}

      {/* Comparison table */}
      {selectedOffices.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-400">Select offices above to compare their performance</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Metric</th>
                  {officeData.map(data => (
                    <th key={data.officeId} className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                      {data.office?.name || data.officeId}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {/* Broker Count */}
                <tr className="hover:bg-gray-750">
                  <td className="px-6 py-4 text-sm text-gray-300">Broker Count</td>
                  {officeData.map(data => (
                    <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-medium">
                      {data.loading ? '...' : data.brokerCount}
                    </td>
                  ))}
                </tr>

                {/* Upfront Commission */}
                <tr className="hover:bg-gray-750">
                  <td className="px-6 py-4 text-sm text-gray-300">Upfront Commission</td>
                  {officeData.map(data => (
                    <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-medium">
                      {hideCommissions ? '\u2014' : data.loading ? '...' : formatCurrencyShorthand(data.upfrontTotal)}
                    </td>
                  ))}
                </tr>

                {/* Trail Commission */}
                <tr className="hover:bg-gray-750">
                  <td className="px-6 py-4 text-sm text-gray-300">Trail Commission</td>
                  {officeData.map(data => (
                    <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-medium">
                      {hideCommissions ? '\u2014' : data.loading ? '...' : formatCurrencyShorthand(data.trailTotal)}
                    </td>
                  ))}
                </tr>

                {/* Total Commission */}
                <tr className="hover:bg-gray-750 bg-gray-750/50">
                  <td className="px-6 py-4 text-sm font-semibold text-white">Total Commission</td>
                  {officeData.map(data => (
                    <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-bold">
                      {hideCommissions ? '\u2014' : data.loading ? '...' : formatCurrencyShorthand(data.totalCommission)}
                    </td>
                  ))}
                </tr>

                {/* Loan Book - loan VOLUME, so it survives the portfolio totals switch. */}
                <tr className="hover:bg-gray-750">
                  <td className="px-6 py-4 text-sm text-gray-300">Loan Book</td>
                  {officeData.map(data => (
                    <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-medium">
                      {hideLoanVolume ? '\u2014' : data.loading ? '...' : formatCurrencyShorthand(data.totalLoanBook)}
                    </td>
                  ))}
                </tr>

                {/* Line of Trail */}
                <tr className="hover:bg-gray-750">
                  <td className="px-6 py-4 text-sm text-gray-300">Line of Trail (loans)</td>
                  {officeData.map(data => (
                    <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-medium">
                      {data.loading ? '...' : data.lineOfTrail.toLocaleString()}
                    </td>
                  ))}
                </tr>

                {/* Avg Commission per Broker */}
                <tr className="hover:bg-gray-750">
                  <td className="px-6 py-4 text-sm text-gray-300">Avg Commission / Broker</td>
                  {officeData.map(data => {
                    const avg = data.brokerCount > 0 ? data.totalCommission / data.brokerCount : 0;
                    return (
                      <td key={data.officeId} className="px-6 py-4 text-right text-sm text-white font-medium">
                        {hideCommissions ? '\u2014' : data.loading ? '...' : formatCurrencyShorthand(avg)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Winner highlights */}
      {selectedOffices.length > 1 && officeData.every(d => !d.loading) && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Highest total commission - the winning office is itself picked by
              commission ranking, so the whole card goes for a hidden user;
              showing only the name would still disclose the ordering. */}
          {!hideCommissions && (() => {
            const winner = officeData.reduce((max, curr) =>
              curr.totalCommission > max.totalCommission ? curr : max
            );
            return (
              <div className="bg-gradient-to-br from-green-900/30 to-gray-800 border border-green-500/30 rounded-lg p-4">
                <div className="text-green-400 text-sm font-medium mb-1">Highest Total Commission</div>
                <div className="text-white text-lg font-bold">{winner.office?.name}</div>
                <div className="text-gray-300 text-sm">{formatCurrencyShorthand(winner.totalCommission)}</div>
              </div>
            );
          })()}

          {/* Highest loan book - loan VOLUME ranking, so it survives the
              portfolio totals switch. */}
          {!hideLoanVolume && (() => {
            const winner = officeData.reduce((max, curr) =>
              curr.totalLoanBook > max.totalLoanBook ? curr : max
            );
            return (
              <div className="bg-gradient-to-br from-blue-900/30 to-gray-800 border border-blue-500/30 rounded-lg p-4">
                <div className="text-blue-400 text-sm font-medium mb-1">Largest Loan Book</div>
                <div className="text-white text-lg font-bold">{winner.office?.name}</div>
                <div className="text-gray-300 text-sm">{formatCurrencyShorthand(winner.totalLoanBook)}</div>
              </div>
            );
          })()}

          {/* Highest per-broker average - commission-derived ranking. */}
          {!hideCommissions && (() => {
            const winner = officeData.reduce((max, curr) => {
              const maxAvg = max.brokerCount > 0 ? max.totalCommission / max.brokerCount : 0;
              const currAvg = curr.brokerCount > 0 ? curr.totalCommission / curr.brokerCount : 0;
              return currAvg > maxAvg ? curr : max;
            });
            const avg = winner.brokerCount > 0 ? winner.totalCommission / winner.brokerCount : 0;
            return (
              <div className="bg-gradient-to-br from-purple-900/30 to-gray-800 border border-purple-500/30 rounded-lg p-4">
                <div className="text-purple-400 text-sm font-medium mb-1">Best Per-Broker Performance</div>
                <div className="text-white text-lg font-bold">{winner.office?.name}</div>
                <div className="text-gray-300 text-sm">{formatCurrencyShorthand(avg)} / broker</div>
              </div>
            );
          })()}
        </div>
      )}
      </div>
    </div>
  );
};

export default OfficeComparisonPage;
